import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    ArrowLeft,
    Download,
    Filter,
    Search,
    User,
    X,
} from "lucide-react";

import PageLayout from "../components/PageLayout";
import { useAuth } from "../context/AuthContext";
import { getAllDesignations, getAllZones } from "../api/master";
import { getAllEmployees } from "../api/employee";
import {
    downloadExpenseCSV,
    getEmployeeExpenseDetail,
    getExpenseSummary,
    type ExpenseFilters,
} from "../api/expense";

// ── Types ─────────────────────────────────────────────────────────────────

interface EmployeeRow {
    employee_id: number;
    name: string;
    email: string;
    code: string;
    designation: string;
    zone: string;
    da_total: number;
    ta_total: number;
    total: number;
}

interface GrandTotal {
    da_total: number;
    ta_total: number;
    total: number;
}

interface DailyRecord {
    date: string;
    da_amount: number;
    da_status: string | null;
    da_location_type: string | null;
    ta_amount: number;
    ta_status: string | null;
    ta_route: string | null;
    ta_mode: string | null;
    ta_distance: number | null;
    ta_type: number | null;
    total: number;
}

interface EmployeeDetail {
    employee: { id: number; name: string; email: string; code: string; designation: string; zone: string };
    summary: { da_total: number; ta_total: number; total: number };
    records: DailyRecord[];
}

// ── Helpers ───────────────────────────────────────────────────────────────

const fmt = (n: number) =>
    new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const locationLabel: Record<string, string> = {
    hq: "Headquarter",
    ex: "Ex-Station",
    out: "Out-Station",
    transit: "In Transit",
    other: "Other",
};

const statusBadge = (status: string | null) => {
    if (!status) return null;
    const map: Record<string, string> = {
        pending:  "bg-yellow-100 text-yellow-800",
        approved: "bg-green-100  text-green-800",
        paid:     "bg-blue-100   text-blue-800",
    };
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${map[status] ?? "bg-gray-100 text-gray-700"}`}>
            {status}
        </span>
    );
};

// ta_type: 1 = one-way, 2 = two-way (see AttendanceService::resolveOutStationTaType)
const taTypeBadge = (taType: number | null) => {
    if (taType !== 1 && taType !== 2) return <span className="text-gray-400">—</span>;
    const isOneWay = taType === 1;
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isOneWay ? "bg-purple-100 text-purple-800" : "bg-teal-100 text-teal-800"}`}>
            {isOneWay ? "One-way" : "Two-way"}
        </span>
    );
};

// ── Summary Cards ─────────────────────────────────────────────────────────

const SummaryCards = ({ da, ta, total }: { da: number; ta: number; total: number }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Daily Allowance (DA)</p>
            <p className="text-2xl font-bold text-gray-900">₹{fmt(da)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Travel Allowance (TA)</p>
            <p className="text-2xl font-bold text-gray-900">₹{fmt(ta)}</p>
        </div>
        <div className="bg-blue-600 rounded-xl p-5 shadow-sm">
            <p className="text-xs font-medium text-blue-100 uppercase tracking-wide mb-1">Total Expenses</p>
            <p className="text-2xl font-bold text-white">₹{fmt(total)}</p>
        </div>
    </div>
);

// ── Skeleton ──────────────────────────────────────────────────────────────

const Skeleton = ({ rows = 5 }: { rows?: number }) => (
    <div className="animate-pulse space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg" />
        ))}
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────

interface ExpensesPageProps {
    onLogout: () => void;
    onNavigate: (page: string) => void;
}

const ExpensesPage: React.FC<ExpensesPageProps> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL-driven employee detail — ?employee_id=X
    const urlEmployeeId = searchParams.get("employee_id");

    // ── Filter state ──────────────────────────────────────────────────────
    const [filters, setFilters] = useState<ExpenseFilters>({
        zone_id:        "",
        designation_id: "",
        employee_id:    urlEmployeeId ?? "",
        from_date:      "",
        to_date:        "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [search,      setSearch]      = useState("");

    // ── Reference data ────────────────────────────────────────────────────
    const [zones,        setZones]        = useState<any[]>([]);
    const [designations, setDesignations] = useState<any[]>([]);
    const [employees,    setEmployees]    = useState<any[]>([]);

    // ── Overview data ─────────────────────────────────────────────────────
    const [rows,       setRows]       = useState<EmployeeRow[]>([]);
    const [grandTotal, setGrandTotal] = useState<GrandTotal>({ da_total: 0, ta_total: 0, total: 0 });
    const [loading,    setLoading]    = useState(false);

    // ── Detail data ───────────────────────────────────────────────────────
    const [detail,         setDetail]        = useState<EmployeeDetail | null>(null);
    const [detailLoading,  setDetailLoading] = useState(false);
    const [detailFilters,  setDetailFilters] = useState({ from_date: "", to_date: "" });

    // ── Exporting ─────────────────────────────────────────────────────────
    const [exporting, setExporting] = useState(false);

    const tenantId = user?.tenant_id;

    // ── Load reference data once ──────────────────────────────────────────
    useEffect(() => {
        if (!tenantId) return;
        Promise.all([
            getAllZones(tenantId),
            getAllDesignations(tenantId),
            getAllEmployees(tenantId),
        ]).then(([z, d, e]) => {
            setZones(z ?? []);
            setDesignations(d ?? []);
            setEmployees(e ?? []);
        });
    }, [tenantId]);

    // ── Load summary / overview ───────────────────────────────────────────
    const loadSummary = useCallback(async (f: ExpenseFilters) => {
        if (!tenantId) return;
        setLoading(true);
        try {
            const res = await getExpenseSummary(tenantId, f);
            if (res.data?.data) {
                setRows(res.data.data.employees ?? []);
                setGrandTotal(res.data.data.grand_total ?? { da_total: 0, ta_total: 0, total: 0 });
            }
        } catch (e) {
            console.error("loadSummary", e);
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    // ── Load employee detail ──────────────────────────────────────────────
    const loadDetail = useCallback(async (empId: number | string, f: { from_date?: string; to_date?: string }) => {
        if (!tenantId) return;
        setDetailLoading(true);
        try {
            const res = await getEmployeeExpenseDetail(tenantId, empId, f);
            if (res.data?.data) setDetail(res.data.data);
        } catch (e) {
            console.error("loadDetail", e);
        } finally {
            setDetailLoading(false);
        }
    }, [tenantId]);

    // Initial load
    useEffect(() => {
        if (urlEmployeeId) {
            loadDetail(urlEmployeeId, {});
        } else {
            loadSummary(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId]);

    // ── Handlers ──────────────────────────────────────────────────────────

    const applyFilters = () => {
        if (urlEmployeeId) {
            loadDetail(urlEmployeeId, detailFilters);
        } else {
            loadSummary(filters);
        }
    };

    const clearFilters = () => {
        const empty = { zone_id: "", designation_id: "", employee_id: "", from_date: "", to_date: "" };
        setFilters(empty);
        loadSummary(empty);
    };

    const openDetail = (empId: number) => {
        setSearchParams({ employee_id: String(empId) });
        loadDetail(empId, {});
    };

    const closeDetail = () => {
        setSearchParams({});
        setDetail(null);
        loadSummary(filters);
    };

    const handleExport = async () => {
        if (!tenantId) return;
        setExporting(true);
        try {
            await downloadExpenseCSV(tenantId, urlEmployeeId ? { employee_id: urlEmployeeId } : filters);
        } catch (e) {
            console.error("export", e);
        } finally {
            setExporting(false);
        }
    };

    // ── Filtered rows (client-side search) ────────────────────────────────
    const visibleRows = rows.filter(r => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            r.name.toLowerCase().includes(q) ||
            r.code.toLowerCase().includes(q) ||
            r.designation.toLowerCase().includes(q) ||
            r.zone.toLowerCase().includes(q)
        );
    });

    const isDetailMode = !!urlEmployeeId;

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <PageLayout onLogout={onLogout} currentPage="expenses" onNavigate={onNavigate}>
            <div className="p-4 md:p-8">
                    <div className="max-w-7xl mx-auto">

                        {/* ── Header ── */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                            <div className="flex items-center gap-3">
                                {isDetailMode && (
                                    <button
                                        onClick={closeDetail}
                                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                )}
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {isDetailMode ? (detail?.employee.name ?? "Employee Expenses") : "Expense Management"}
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {isDetailMode
                                            ? `${detail?.employee.designation ?? ""} · ${detail?.employee.zone ?? ""}`
                                            : "DA & TA overview across all employees"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                {!isDetailMode && (
                                    <button
                                        onClick={() => setShowFilters(v => !v)}
                                        className={`flex items-center px-4 py-2 border rounded-lg transition-colors text-sm ${
                                            showFilters ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        <Filter className="w-4 h-4 mr-2" />
                                        Filters
                                    </button>
                                )}
                                <button
                                    onClick={handleExport}
                                    disabled={exporting}
                                    className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors text-sm"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    {exporting ? "Exporting…" : "Export CSV"}
                                </button>
                            </div>
                        </div>

                        {/* ── Overview mode ── */}
                        {!isDetailMode && (
                            <>
                                {/* Filter panel */}
                                {showFilters && (
                                    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                                                <select
                                                    value={filters.zone_id ?? ""}
                                                    onChange={e => setFilters(p => ({ ...p, zone_id: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="">All Zones</option>
                                                    {zones.map((z: any) => (
                                                        <option key={z.id} value={z.id}>{z.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Designation</label>
                                                <select
                                                    value={filters.designation_id ?? ""}
                                                    onChange={e => setFilters(p => ({ ...p, designation_id: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="">All Designations</option>
                                                    {designations.map((d: any) => (
                                                        <option key={d.id} value={d.id}>{d.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                                                <select
                                                    value={filters.employee_id ?? ""}
                                                    onChange={e => setFilters(p => ({ ...p, employee_id: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="">All Employees</option>
                                                    {employees.map((emp: any) => (
                                                        <option key={emp.id} value={emp.id}>{emp.user?.name ?? `#${emp.id}`}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    value={filters.from_date ?? ""}
                                                    onChange={e => setFilters(p => ({ ...p, from_date: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    value={filters.to_date ?? ""}
                                                    onChange={e => setFilters(p => ({ ...p, to_date: e.target.value }))}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-3 mt-4">
                                            <button
                                                onClick={applyFilters}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                            >
                                                Apply Filters
                                            </button>
                                            <button
                                                onClick={clearFilters}
                                                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                                            >
                                                <X className="w-3 h-3 mr-1" /> Clear
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Summary cards */}
                                <SummaryCards da={grandTotal.da_total} ta={grandTotal.ta_total} total={grandTotal.total} />

                                {/* Search bar */}
                                <div className="relative mb-4">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, code, designation, zone…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                {/* Table */}
                                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                    {loading ? (
                                        <div className="p-6"><Skeleton /></div>
                                    ) : visibleRows.length === 0 ? (
                                        <div className="p-12 text-center">
                                            <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500 text-sm">No expense data found for the selected filters.</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        {["#", "Employee", "Code", "Designation", "Zone", "DA (₹)", "TA (₹)", "Total (₹)", ""].map(h => (
                                                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                                {h}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {visibleRows.map((row, idx) => (
                                                        <tr key={row.employee_id} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-5 py-3.5 text-sm text-gray-400">{idx + 1}</td>
                                                            <td className="px-5 py-3.5">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xs flex-shrink-0">
                                                                        {row.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{row.name}</p>
                                                                        <p className="text-xs text-gray-400">{row.email}</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">{row.code || "—"}</td>
                                                            <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">{row.designation}</td>
                                                            <td className="px-5 py-3.5 text-sm text-gray-600 whitespace-nowrap">{row.zone}</td>
                                                            <td className="px-5 py-3.5 text-sm text-gray-700 font-medium whitespace-nowrap">₹{fmt(row.da_total)}</td>
                                                            <td className="px-5 py-3.5 text-sm text-gray-700 font-medium whitespace-nowrap">₹{fmt(row.ta_total)}</td>
                                                            <td className="px-5 py-3.5 text-sm font-semibold text-blue-700 whitespace-nowrap">₹{fmt(row.total)}</td>
                                                            <td className="px-5 py-3.5">
                                                                <button
                                                                    onClick={() => openDetail(row.employee_id)}
                                                                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
                                                                >
                                                                    View Details
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                                    <tr>
                                                        <td colSpan={5} className="px-5 py-3 text-sm font-semibold text-gray-700">
                                                            Grand Total ({visibleRows.length} employees)
                                                        </td>
                                                        <td className="px-5 py-3 text-sm font-bold text-gray-900">
                                                            ₹{fmt(visibleRows.reduce((s, r) => s + r.da_total, 0))}
                                                        </td>
                                                        <td className="px-5 py-3 text-sm font-bold text-gray-900">
                                                            ₹{fmt(visibleRows.reduce((s, r) => s + r.ta_total, 0))}
                                                        </td>
                                                        <td className="px-5 py-3 text-sm font-bold text-blue-700">
                                                            ₹{fmt(visibleRows.reduce((s, r) => s + r.total, 0))}
                                                        </td>
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* ── Detail mode ── */}
                        {isDetailMode && (
                            <>
                                {/* Date filter for detail */}
                                <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
                                    <div className="flex flex-wrap gap-4 items-end">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                                            <input
                                                type="date"
                                                value={detailFilters.from_date}
                                                onChange={e => setDetailFilters(p => ({ ...p, from_date: e.target.value }))}
                                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                                            <input
                                                type="date"
                                                value={detailFilters.to_date}
                                                onChange={e => setDetailFilters(p => ({ ...p, to_date: e.target.value }))}
                                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <button
                                            onClick={applyFilters}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                                        >
                                            Apply
                                        </button>
                                        {(detailFilters.from_date || detailFilters.to_date) && (
                                            <button
                                                onClick={() => {
                                                    setDetailFilters({ from_date: "", to_date: "" });
                                                    loadDetail(urlEmployeeId!, {});
                                                }}
                                                className="flex items-center text-sm text-gray-500 hover:text-gray-700"
                                            >
                                                <X className="w-3 h-3 mr-1" /> Clear
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {detailLoading ? (
                                    <div className="bg-white rounded-xl border border-gray-200 p-6"><Skeleton rows={8} /></div>
                                ) : !detail ? (
                                    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
                                        No expense data found.
                                    </div>
                                ) : (
                                    <>
                                        <SummaryCards da={detail.summary.da_total} ta={detail.summary.ta_total} total={detail.summary.total} />

                                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                            <div className="px-5 py-4 border-b border-gray-100">
                                                <h2 className="font-semibold text-gray-800 text-sm">
                                                    Expense Records ({detail.records.length} days)
                                                </h2>
                                            </div>

                                            {detail.records.length === 0 ? (
                                                <div className="p-12 text-center text-gray-500 text-sm">
                                                    No records for the selected period.
                                                </div>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                {["Date", "Location Type", "DA (₹)", "DA Status", "Route", "Mode", "Dist (km)", "Direction", "TA (₹)", "TA Status", "Total (₹)"].map(h => (
                                                                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                                        {h}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {detail.records.map(rec => (
                                                                <tr key={rec.date} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{rec.date}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                                        {rec.da_location_type ? locationLabel[rec.da_location_type] ?? rec.da_location_type : "—"}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">₹{fmt(rec.da_amount)}</td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">{statusBadge(rec.da_status)}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{rec.ta_route ?? "—"}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{rec.ta_mode ?? "—"}</td>
                                                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{rec.ta_distance ?? "—"}</td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">{taTypeBadge(rec.ta_type)}</td>
                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">₹{fmt(rec.ta_amount)}</td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">{statusBadge(rec.ta_status)}</td>
                                                                    <td className="px-4 py-3 text-sm font-semibold text-blue-700 whitespace-nowrap">₹{fmt(rec.total)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                                            <tr>
                                                                <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-700">Total</td>
                                                                <td className="px-4 py-3 text-sm font-bold text-gray-900">₹{fmt(detail.summary.da_total)}</td>
                                                                <td />
                                                                <td colSpan={4} />
                                                                <td className="px-4 py-3 text-sm font-bold text-gray-900">₹{fmt(detail.summary.ta_total)}</td>
                                                                <td />
                                                                <td className="px-4 py-3 text-sm font-bold text-blue-700">₹{fmt(detail.summary.total)}</td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
        </PageLayout>
    );
};

export default ExpensesPage;
